import { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Masonry from 'react-masonry-css';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  STATISTICS_MASONRY_BREAKPOINTS,
  StatisticsChartsMasonry,
} from './statisticsChartsMasonry';
import PageHeader from '../PageHeader';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { TransferHistory } from './Types/transferHistory';
import TransferHistoryCard from './TransferHistoryCard';
import TransferDistributionCard from './TransferDistributionCard';
import TransferTimeEvolutionCard from './TransferEvolutionCard';
import FeedbackCard from '../Elements/FeedbackCard';
import { getApi } from '../../utils/api';
import { getLineName } from '../../utils/mapJourneysData';
import { useTranslation } from 'react-i18next';
import {
  deserializeTransferStatsFromSearchParams,
  serializeTransferStatsToSearchParams,
  buildLegStubsFromTransferUrlPayload,
  transferStatsPayloadFromLegs,
} from '../../utils/transferStatsUrlParams';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 32px;
  @media (min-width: 768px) {
    padding: 36px 64px;  // Increased horizontal padding for desktop
  }
`;

const ChartsContainer = StatisticsChartsMasonry;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  align-self: stretch;
  padding: 1rem;
  background: white;
  border-bottom: 2px solid ${slateColors[200]};
  
  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
    gap: 0.75rem;
  }
`;

type TransferHistoryResponse = TransferHistory[];

const TransferStatistics = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPayload = useMemo(
    () => deserializeTransferStatsFromSearchParams(searchParams),
    [searchParams]
  );

  const firstLegFromState = location.state?.firstLeg;
  const secondLegFromState = location.state?.secondLeg;
  const fromPath = (location.state?.from as string) || '';

  const { firstLeg, secondLeg } = useMemo(() => {
    if (urlPayload) {
      return buildLegStubsFromTransferUrlPayload(urlPayload);
    }
    if (firstLegFromState && secondLegFromState) {
      return { firstLeg: firstLegFromState, secondLeg: secondLegFromState };
    }
    return { firstLeg: null, secondLeg: null };
  }, [urlPayload, firstLegFromState, secondLegFromState]);

  /**
   * Breadcrumbs: shared URLs have no `location.state.from` → only Home + title (no dead links).
   * In-app, `from` is set (e.g. `/journey/:id`) → show Journeys + Journey details like the journey screen.
   */
  const transferBreadcrumbs = useMemo(() => {
    const crumbs: Array<{ label: string; path?: string; active?: boolean }> = [
      { label: t('common.home'), path: '/' },
    ];
    const inApp = Boolean(fromPath);
    if (inApp) {
      crumbs.push({ label: t('pageHeader.journeys'), path: '/journeys' });
    }
    if (fromPath.startsWith('/journey/')) {
      const jid = fromPath.split('/journey/')[1]?.split('/')[0];
      if (jid) {
        crumbs.push({
          label: t('statistics.trainStatistics.journeyDetails'),
          path: `/journey/${jid}`,
        });
      }
    }
    crumbs.push({ label: t('statistics.transferStatistics.title'), active: true });
    return crumbs;
  }, [fromPath, t]);

  const hasSyncedUrlRef = useRef(false);
  useEffect(() => {
    if (deserializeTransferStatsFromSearchParams(searchParams)) return;
    if (hasSyncedUrlRef.current) return;
    if (!firstLegFromState || !secondLegFromState) return;
    hasSyncedUrlRef.current = true;
    setSearchParams(
      serializeTransferStatsToSearchParams(
        transferStatsPayloadFromLegs(firstLegFromState, secondLegFromState)
      ),
      { replace: true }
    );
  }, [searchParams, firstLegFromState, secondLegFromState, setSearchParams]);

  const [transferHistoryData, setTransferHistoryData] = useState<TransferHistoryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!firstLeg || !secondLeg) {
      setTransferHistoryData(null);
      return;
    }

    const firstLegLineName = firstLeg.line.name;
    const firstLegDestinationId = firstLeg.destination.id;
    const secondLegLineName = secondLeg.line.name;
    const secondLegOriginId = secondLeg.origin.id;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getApi('transferhistory', {
          firstLegLineName,
          firstLegDestinationId,
          secondLegLineName,
          secondLegOriginId,
        });

        if (data.error) {
          setTransferHistoryData(null);
        } else {
          setTransferHistoryData(data);
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [firstLeg, secondLeg]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hasLegs = Boolean(firstLeg && secondLeg);

  const helmetTitle = hasLegs
    ? `${getLineName(firstLeg)} → ${getLineName(secondLeg)} at ${firstLeg.destination.name} | Transfer Statistics | chuuchuu`
    : 'Transfer Statistics | chuuchuu';
  const helmetDesc = hasLegs
    ? `Transfer reliability data for ${getLineName(firstLeg)} to ${getLineName(secondLeg)} at ${firstLeg.destination.name}. See how often this connection is made on time.`
    : 'Explore transfer reliability data between European train connections.';

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDesc} />
      </Helmet>
      <HeaderContainer>
        <PageHeader
          breadcrumbs={transferBreadcrumbs}
          title={t('statistics.transferStatistics.title')}
          subtitle={hasLegs
            ? `${getLineName(firstLeg)} ➜ ${getLineName(secondLeg)} at ${firstLeg.destination.name}`
            : ''}
        />
      </HeaderContainer>
      <Container>
        {!hasLegs && (
          <div>{t('statistics.transferStatistics.missingLinkParams')}</div>
        )}
        {loading && <div>{t('common.loading')}</div>}
        {error && <div>{t('common.error')}: {error}</div>}
        <ChartsContainer>
          <Masonry
            breakpointCols={STATISTICS_MASONRY_BREAKPOINTS}
            className="statistics-masonry-grid"
            columnClassName="statistics-masonry-grid_column"
          >
            {transferHistoryData && (
              <TransferDistributionCard transferHistoryData={transferHistoryData} />
            )}
            {transferHistoryData && (
              <TransferTimeEvolutionCard transferHistoryData={transferHistoryData} />
            )}
            {transferHistoryData && (
              <TransferHistoryCard transferHistoryData={transferHistoryData} />
            )}
            {hasLegs && (
              <FeedbackCard
                source={`Transfer statistics from ${getLineName(firstLeg)} to ${getLineName(secondLeg)} at ${firstLeg.destination.name}`}
              />
            )}
          </Masonry>
        </ChartsContainer>
      </Container>
    </>
  );
};

export default TransferStatistics;
